import { ElementCategoryID } from '@shared/models/element-categories.model';
import { ElementPropertyName } from '@shared/models/element-property.model';
import {
  ElementKind,
  type Baseline,
  type IElement,
} from '@shared/models/project.model';
import type { ProposalId } from '@shared/models/proposals.model';
import type { SbefCode } from '@shared/construction/sbef.model';
import { SbefGroupCode } from '@shared/construction/sbef.model';
import { createElementWithCategory } from './element-category.helpers';
import { setPropertyCountByNameOrId } from './element-property.helpers';
import { assertUnreachable } from './function.helpers';
import {
  getActiveRootElementIdsInProposal,
  getProposalById,
} from './proposal.helpers';
import { projectGuards } from './guards/project-guards.helpers';
import { getElementById } from './recursive-element.helpers';
import {
  buildElementPathIndex,
  getElementByPath,
} from './pipeline/batch-update.helpers';
import {
  getSbefCode,
  getSbefCodeFromElementOrRecipe,
  getSbefEntryById,
  getSbefGroupCode,
} from './sbef.helpers';
import {
  ProjectChangeType,
  type ProjectChange,
  type ProjectChangeMap,
} from './pipeline/update-engine.models';

/**
 * Shown when the selected proposal id is not on this version.
 * The new rows are dropped rather than written onto baseline by mistake.
 */
export const IMPORT_REPLACE_NEEDS_PROPOSAL_MESSAGE =
  'Select a proposal to import into. Baseline was left unchanged.';

const wall = ElementCategoryID.Wall;
const slab = ElementCategoryID.Slab;
const column = ElementCategoryID.Column;
const beam = ElementCategoryID.Beam;
const other = ElementCategoryID.Other;

/**
 * System element for each SBEF code. Building fabric is a wall or a slab.
 * Column and beam are used only where the code is that member. Other is limited
 * to demolition, loose equipment, installations, and site operations.
 */
const SYSTEM_CATEGORY_BY_SBEF_CODE: Record<SbefCode, ElementCategoryID> = {
  '0': other,
  '00': other,
  '01': other,
  '02': other,
  '03': other,
  '04': other,
  '05': other,
  '06': other,
  '07': other,
  '08': other,
  '09': other,
  '1': slab,
  '10': slab,
  '11': other,
  '12': slab,
  '13': slab,
  '14': slab,
  '15': slab,
  '16': slab,
  '17': slab,
  '18': wall,
  '19': slab,
  '2': slab,
  '20': slab,
  '21': slab,
  '22': slab,
  '23': slab,
  '24': slab,
  '25': slab,
  '26': slab,
  '27': slab,
  '28': slab,
  '29': slab,
  '3': wall,
  '30': wall,
  '31': wall,
  '32': column,
  '33': wall,
  '34': slab,
  '35': beam,
  '36': wall,
  '37': slab,
  '38': wall,
  '39': wall,
  '4': slab,
  '40': slab,
  '41': slab,
  '42': slab,
  '43': slab,
  '44': wall,
  '45': slab,
  '46': slab,
  '47': slab,
  '48': slab,
  '49': slab,
  '5': wall,
  '50': wall,
  '51': wall,
  '52': wall,
  '53': wall,
  '54': wall,
  '55': wall,
  '56': wall,
  '57': wall,
  '58': wall,
  '59': wall,
  '6': wall,
  '60': wall,
  '61': wall,
  '62': slab,
  '63': wall,
  '64': slab,
  '65': wall,
  '66': wall,
  '67': wall,
  '68': wall,
  '69': wall,
  '7': wall,
  '70': wall,
  '71': wall,
  '72': slab,
  '73': wall,
  '74': slab,
  '75': wall,
  '76': other,
  '77': wall,
  '78': wall,
  '79': wall,
  '8': other,
  '80': other,
  '81': other,
  '82': other,
  '83': other,
  '84': other,
  '85': other,
  '86': other,
  '87': other,
  '88': other,
  '89': other,
  '9': other,
  '90': other,
  '91': other,
  '92': other,
  '93': other,
  '94': other,
  '95': other,
  '96': other,
  '97': other,
  '98': other,
  '99': other,
  '101': other,
  '102': other,
  '103': other,
  '104': other,
  '105': other,
};

/**
 * Replacement bucket for one imported SBEF code.
 * `site-other` is SBEF none and `9` (Other under Site operations).
 * `code` is an exact code: `24` does not match group `2`, and `102`–`105` stay on their own code.
 */
export type ImportReplaceKey =
  { kind: 'site-other' } | { kind: 'code'; code: SbefCode };

type ElementAddChange = ProjectChangeMap[ProjectChangeType.ElementAdd];

type ImportBucket = {
  key: ImportReplaceKey;
  elements: IElement[];
};

/**
 * Code stored on the parent and used in its name. Site-operations Other is `9`.
 */
export const importReplaceKeyId = (key: ImportReplaceKey): string => {
  switch (key.kind) {
    case 'site-other':
      return SbefGroupCode.CommonWorks;
    case 'code':
      return key.code;
    default:
      return assertUnreachable(key);
  }
};

/**
 * Bucket for an imported element's own SBEF code.
 * Missing code and `9` are Other under Site operations. Every other code, including `102`–`105`, is exact.
 */
export const replaceKeyForElement = (element: IElement): ImportReplaceKey => {
  const code = getSbefCodeFromElementOrRecipe(element);
  if (!code || code === SbefGroupCode.CommonWorks)
    return { kind: 'site-other' };
  return { kind: 'code', code };
};

/**
 * Whether an existing root element's SBEF code is the one this import replaces.
 * Site-operations Other matches a missing code or `9` only — not `90`–`99` and not `102`–`105`.
 */
export const elementMatchesImportReplaceKey = (
  code: SbefCode | undefined,
  key: ImportReplaceKey,
): boolean => {
  switch (key.kind) {
    case 'site-other':
      return code === undefined || code === SbefGroupCode.CommonWorks;
    case 'code':
      return code === key.code;
    default:
      return assertUnreachable(key);
  }
};

/**
 * System category for the parent that holds one imported SBEF code.
 */
export const systemCategoryForImportReplaceKey = (
  key: ImportReplaceKey,
): ElementCategoryID => {
  switch (key.kind) {
    case 'site-other':
      return ElementCategoryID.Other;
    case 'code':
      return SYSTEM_CATEGORY_BY_SBEF_CODE[key.code];
    default:
      return assertUnreachable(key);
  }
};

/**
 * Parent label: the SBEF number, then the code's own name (`31. Frame walls`).
 * A code with no name uses its group's name.
 */
export const buildImportParentName = (code: string): string => {
  const sbef = getSbefCode(code);
  if (!sbef) return code;
  const ownName = getSbefEntryById(sbef)?.name.trim();
  if (ownName) return `${sbef}. ${ownName}`;
  const group = getSbefGroupCode(sbef);
  const groupName = group ? getSbefEntryById(group)?.name.trim() : undefined;
  if (groupName) return `${sbef}. ${groupName}`;
  return sbef;
};

/**
 * The parent import created for one SBEF code. A group the user made is not
 * one of these, even when it holds imported children and the same code.
 */
const isImportGroupingParent = (element: IElement): boolean =>
  element.importReplaceParent === true;

const isNewImportRootAdd = (
  change: ProjectChange,
  versionId: string,
): change is ElementAddChange => {
  if (change.type !== ProjectChangeType.ElementAdd) return false;
  if (change.elementId !== versionId) return false;
  if (change.regenerateIds === false) return false;
  if (change.factory.kind !== ElementKind.Element) return false;
  return change.factory.origin?.kind === 'csvImport';
};

const bucketKey = (key: ImportReplaceKey): string =>
  key.kind === 'site-other' ? 'site-other' : key.code;

const findImportParent = (
  version: Baseline,
  proposalId: ProposalId | undefined,
  key: ImportReplaceKey,
): IElement | undefined => {
  for (const child of version.elements) {
    if (!projectGuards.is.element(child)) continue;
    // `undefined` is baseline: only a parent with no proposal owner.
    if (child.createdInProposalId !== proposalId) continue;
    if (!isImportGroupingParent(child)) continue;
    if (
      !elementMatchesImportReplaceKey(
        getSbefCodeFromElementOrRecipe(child),
        key,
      )
    ) {
      continue;
    }
    return child;
  }
  return undefined;
};

const createImportParent = (bucket: ImportBucket): IElement => {
  const code = importReplaceKeyId(bucket.key);
  const parent = createElementWithCategory({
    kind: ElementKind.Element,
    category_id: systemCategoryForImportReplaceKey(bucket.key),
    name: buildImportParentName(code),
    elements: bucket.elements,
  });
  return {
    ...setPropertyCountByNameOrId(parent, ElementPropertyName.SbefCode, code),
    importReplaceParent: true,
  };
};

/**
 * Root add for a new import parent. The parent keeps its id so re-adds in the
 * same batch can target it, in baseline as in a proposal.
 */
const importParentAdd = (
  version: Baseline,
  parent: IElement,
): ProjectChange => ({
  type: ProjectChangeType.ElementAdd,
  elementId: version.id,
  factory: parent,
  generateUniqueName: false,
  regenerateIds: { preserveRootElementIds: true },
});

/**
 * Import parents that {@link changes} already adds at the version root, by bucket key.
 */
const importParentAddsInBatch = (
  version: Baseline,
  changes: readonly ProjectChange[],
): Map<string, { index: number; parentId: string }> => {
  const byKey = new Map<string, { index: number; parentId: string }>();
  changes.forEach((change, index) => {
    if (change.type !== ProjectChangeType.ElementAdd) return;
    if (change.elementId !== version.id) return;
    if (!projectGuards.is.element(change.factory)) return;
    if (!isImportGroupingParent(change.factory) || !change.factory.id) return;
    const key = bucketKey(replaceKeyForElement(change.factory));
    if (!byKey.has(key)) byKey.set(key, { index, parentId: change.factory.id });
  });
  return byKey;
};

/**
 * Active root elements in the selected target whose SBEF code (own or via recipe)
 * one of {@link keys} replaces. {@link proposalId} `undefined` is baseline.
 * Earlier imports, and every import parent, are not included.
 * Called when that code's import parent is first created.
 * Called "existing elements" rather than "estimates": Nodon has no estimate
 * concept for elements, and "estimate" already means product market estimates.
 */
const existingElementIdsToDeactivate = (
  version: Baseline,
  proposalId: ProposalId | undefined,
  keys: readonly ImportReplaceKey[],
): string[] => {
  const proposal = proposalId
    ? getProposalById(version, proposalId, false)
    : undefined;
  if (proposalId && !proposal) return [];
  const activeIds = getActiveRootElementIdsInProposal(version, proposal, false);
  const ids: string[] = [];
  for (const child of version.elements) {
    if (!projectGuards.is.element(child) || !activeIds.has(child.id)) continue;
    if (child.createdInProposalId && child.createdInProposalId !== proposalId) {
      continue;
    }
    if (child.origin?.kind === 'csvImport') continue;
    // Any target's import parent is an earlier import, not an existing element.
    if (isImportGroupingParent(child)) continue;
    const code = getSbefCodeFromElementOrRecipe(child);
    if (!keys.some((key) => elementMatchesImportReplaceKey(code, key)))
      continue;
    ids.push(child.id);
  }
  return ids;
};

/**
 * Nest newly materialized import rows under one system parent per SBEF code and
 * deactivate matching existing root elements in {@link proposalId}.
 *
 * Adds that keep their id (`regenerateIds: false`) are left in place — those are
 * re-maps of an element that already exists. {@link proposalId} `undefined` is
 * baseline and uses the same grouping and deactivation, written on the version.
 * A proposal id that is not on the version still drops the rows.
 */
export const groupImportedElementAdds = (
  version: Baseline,
  proposalId: ProposalId | undefined,
  changes: readonly ProjectChange[],
): { changes: ProjectChange[]; needsProposal: boolean } => {
  const importAdds = changes.filter((change) =>
    isNewImportRootAdd(change, version.id),
  );
  if (importAdds.length === 0) {
    return { changes: [...changes], needsProposal: false };
  }

  if (proposalId && !getProposalById(version, proposalId, false)) {
    return {
      changes: changes.filter(
        (change) => !isNewImportRootAdd(change, version.id),
      ),
      needsProposal: true,
    };
  }

  const buckets = new Map<string, ImportBucket>();
  for (const change of importAdds) {
    if (!projectGuards.is.element(change.factory)) continue;
    const element = change.factory;
    const key = replaceKeyForElement(element);
    const id = bucketKey(key);
    const bucket = buckets.get(id) ?? { key, elements: [] };
    bucket.elements.push(element);
    buckets.set(id, bucket);
  }

  const grouped: ProjectChange[] = [];
  const keysToDeactivate: ImportReplaceKey[] = [];
  for (const bucket of buckets.values()) {
    const parent = findImportParent(version, proposalId, bucket.key);
    if (parent) {
      for (const element of bucket.elements) {
        grouped.push({
          type: ProjectChangeType.ElementAdd,
          elementId: parent.id,
          factory: element,
          generateUniqueName: false,
        });
      }
      continue;
    }
    keysToDeactivate.push(bucket.key);
    grouped.push(importParentAdd(version, createImportParent(bucket)));
  }

  for (const elementId of existingElementIdsToDeactivate(
    version,
    proposalId,
    keysToDeactivate,
  )) {
    grouped.push({
      type: ProjectChangeType.ElementDeactivate,
      elementId,
    });
  }

  let inserted = false;
  const next: ProjectChange[] = [];
  for (const change of changes) {
    if (!isNewImportRootAdd(change, version.id)) {
      next.push(change);
      continue;
    }
    if (inserted) continue;
    next.push(...grouped);
    inserted = true;
  }
  return { changes: next, needsProposal: false };
};

/**
 * Parent to re-add an already imported element under. A grouped import stays
 * under its SBEF parent; a root element stays on the version.
 *
 * When the element's SBEF code changed, {@link placeRemappedImportAdds} moves
 * the add onto the parent for the new code instead.
 */
export const importReaddParentId = (
  version: Baseline,
  elementId: string,
): string => {
  // Cached per version: bulk switches call this once per row.
  const indices = buildElementPathIndex(version).get(elementId);
  if (!indices?.length) return version.id;
  return getElementByPath(version, indices.slice(0, -1))?.id ?? version.id;
};

const replaceKeysMatch = (a: ImportReplaceKey, b: ImportReplaceKey): boolean =>
  bucketKey(a) === bucketKey(b);

type RemapMove = {
  index: number;
  key: ImportReplaceKey;
};

/**
 * A re-add whose SBEF code differs from the element already in the tree.
 * Same-code switches keep the parent {@link importReaddParentId} chose.
 */
const remapMoveForAdd = (
  version: Baseline,
  change: ProjectChange,
  index: number,
): RemapMove | undefined => {
  if (change.type !== ProjectChangeType.ElementAdd) return undefined;
  if (change.regenerateIds !== false) return undefined;
  if (!projectGuards.is.element(change.factory)) return undefined;
  if (change.factory.origin?.kind !== 'csvImport') return undefined;
  if (!change.factory.id) return undefined;

  const previous = getElementById(version, change.factory.id, false);
  if (!projectGuards.is.element(previous)) return undefined;

  const nextKey = replaceKeyForElement(change.factory);
  if (replaceKeysMatch(replaceKeyForElement(previous), nextKey))
    return undefined;
  return { index, key: nextKey };
};

/**
 * Move re-added import elements onto the import parent for their new SBEF code.
 * The parent is created when this target does not have one yet, and matching
 * existing elements are deactivated only then. Same-code re-adds are unchanged.
 * {@link proposalId} `undefined` is baseline. A proposal id that is not on the
 * version leaves the adds where they are.
 */
export const placeRemappedImportAdds = (
  version: Baseline,
  proposalId: ProposalId | undefined,
  changes: readonly ProjectChange[],
): ProjectChange[] => {
  if (proposalId && !getProposalById(version, proposalId, false)) {
    return [...changes];
  }

  const moves = changes.flatMap((change, index) => {
    const move = remapMoveForAdd(version, change, index);
    return move ? [move] : [];
  });
  if (moves.length === 0) return [...changes];

  const batchParents = importParentAddsInBatch(version, changes);
  const parentIdByKey = new Map<string, string>();
  const hoistedIndices = new Set<number>();
  const parentAdds: ProjectChange[] = [];
  const keysToDeactivate: ImportReplaceKey[] = [];

  for (const move of moves) {
    const id = bucketKey(move.key);
    if (parentIdByKey.has(id)) continue;
    // Created by {@link groupImportedElementAdds} in this batch. It already
    // deactivated that code; move it first so the re-add finds its parent.
    const inBatch = batchParents.get(id);
    if (inBatch) {
      parentIdByKey.set(id, inBatch.parentId);
      hoistedIndices.add(inBatch.index);
      continue;
    }
    const existing = findImportParent(version, proposalId, move.key);
    if (existing) {
      parentIdByKey.set(id, existing.id);
      continue;
    }
    const parent = createImportParent({ key: move.key, elements: [] });
    parentIdByKey.set(id, parent.id);
    keysToDeactivate.push(move.key);
    parentAdds.push(importParentAdd(version, parent));
  }

  const moveByIndex = new Map(moves.map((move) => [move.index, move]));
  const retargeted = changes.flatMap((change, index) => {
    if (hoistedIndices.has(index)) return [];
    const move = moveByIndex.get(index);
    if (!move || change.type !== ProjectChangeType.ElementAdd) return [change];
    const parentId = parentIdByKey.get(bucketKey(move.key));
    if (!parentId) return [change];
    return [{ ...change, elementId: parentId }];
  });

  const deactivations: ProjectChange[] = existingElementIdsToDeactivate(
    version,
    proposalId,
    keysToDeactivate,
  ).map((elementId) => ({
    type: ProjectChangeType.ElementDeactivate,
    elementId,
  }));

  const hoisted = [...hoistedIndices].flatMap((index) => {
    const change = changes[index];
    return change ? [change] : [];
  });
  return [...hoisted, ...parentAdds, ...retargeted, ...deactivations];
};
